import React, { useEffect } from 'react';
import { push } from 'react-router-redux';
import { get } from 'lodash';
import {
  createAccount,
  getQuotesHistory,
  logEditQuote,
  sendCustomerLoginEmail,
  sendEndorsementEmail,
  uncancelPolicy,
} from '../../redux/policyActions';
import { Box } from '../../../../common/components';
import {
  hasPermission,
  hasRole,
  isOwner,
  isBroker,
} from '../../../auth/redux/authActions';

import getIncludedResource from '../../../../helpers/getIncludedResource';
import displayAddress from '../../../../helpers/displayAddress';
import Endorsements from '../Endorsements/Endorsements';
import CustomerDetails from './components/CustomerDetails';
import Travellers from './components/Travellers';
import History from './components/History';
import Transactions from './components/Transactions/Transactions';
import PolicyHistory from '../PolicyHistory/PolicyHistory';

import { BrokerBanner } from '../../../../common/styledComponents/styledComponents';
import './styles.scss';

import PolicyLabel from './components/Label/Label';
import Logo from './Logo/Logo';
import Premiums from './components/Premiums/Premiums';
import RequiredFields from './components/RequiresFields/RequiredFields';
import Buttons from './components/Buttons/Buttons';
import PersonalAccidentSummary from './components/PersonalAccidentSummary/PersonalAccidentSummary';
import LeisureTravelSummary from './components/LeisureTravelSummary/LeisureTravelSummary';
import getStatus from '../../../../schemas/policy';
import { differenceInMinutes, endOfDay, isPast, parseISO } from 'date-fns';
import SmartLuggageSummary from './components/SmartLuggageSummary/SmartLuggageSummary';
import { connect } from 'react-redux';

const SummaryMap = {
  HCP: PersonalAccidentSummary,
  SC: PersonalAccidentSummary,
  PA: PersonalAccidentSummary,
  CC: PersonalAccidentSummary,
  TRAV: LeisureTravelSummary,
  SL: SmartLuggageSummary,
};

const getDerivedStatus = (quoteType, status) => {
  if (quoteType === 'mid term adjustment' && status === 'quote') {
    return 'awaiting approval';
  }
  return getStatus(status);
};

const Summary = ({ productCode, resource, policy, premiums, status }) => {
  const SummaryComponent = SummaryMap[productCode];
  if (!SummaryComponent) {
    return null;
  }

  const ip = premiums.find(
    (premium) => premium.attributes.premium_type === 'initial premium',
  );
  const quoteDate = ip ? ip.attributes.created_at : resource.created_at;

  return (
    <SummaryComponent
      resource={resource}
      policy={policy}
      premiums={premiums}
      status={status}
      quoteDate={quoteDate}
    />
  );
};

const showButtons = (isValid, isStartDateInPast, quoteType, status) => {
  if (!isValid) {
    return false;
  }
  if (isStartDateInPast) {
    return false;
  }

  if (quoteType === 'mid term adjustment' && !hasPermission('policy.mta')) {
    return false;
  }

  return status === 'quote' || status === 'referral approved';
};

const PolicyDetails = ({
  resource,
  reference,
  isBrokerPolicy,
  quotesHistory,
  dispatch,
}) => {
  const handleCreateUser = (customerId) => {
    dispatch(createAccount(customerId));
  };

  const handleCustomerLogin = () => {
    dispatch(
      sendCustomerLoginEmail(
        resource.data.attributes.policy_number ? 'policies' : 'policies/applications',
        resource.data.id,
      ),
    );
  };

  useEffect(
    () => {
      const quoteStatus = get(resource, 'data.attributes.status');

      if (quoteStatus === 'quote') {
        const quoteReference = get(resource, 'data.attributes.quote_reference');
        dispatch(getQuotesHistory(quoteReference));
      }
    },
    [dispatch, resource],
  );

  const handleEndorsementEmail = () => {
    dispatch(sendEndorsementEmail(resource.data.id));
  };

  const handleUncancel = () => {
    dispatch(uncancelPolicy(resource));
  };

  const handleEditQuote = (product) => {
    dispatch(logEditQuote(resource)).then(() => {
      dispatch(push('/products/' + product.data.id + '/quote/' + resource.data.id));
    });
  };

  const declarationText = {
    main:
      'This insurance is only available to residents of the UK & Channel Islands \n\n You must have have resided in the UK for no less than 6 months, have a permanent UK address and be registered with the UK General Practitioner. \n\n This insurance must be purchase prior to you leaving the UK on your planned trip',
    information:
      'There are certain disclosures we are required to make at this stage, in relation to our status, terms of business, our complaints procedure and the fact that we are subject to the Financial Services Compensation scheme. All of this information will be provided to you together with the documents we are sending in the post/via/email/about to give you now.',
    question:
      'Would you like to go through all this information now or are you happy wait to receive it in writing?',
  };
  // Get included resources
  const includedResources = resource.included || [];

  const customer = getIncludedResource(resource.data, includedResources, 'customer');
  const campaign = getIncludedResource(resource.data, includedResources, 'campaign');
  const channel = getIncludedResource(resource.data, includedResources, 'channel');

  const premiums = getIncludedResource(resource.data, includedResources, 'premiums');
  const applications = getIncludedResource(
    resource.data,
    includedResources,
    'issued_applications',
  );

  const sortedIssuedApplications = Array.isArray(applications)
    ? applications.sort((applicationA, applicationB) => {
        return differenceInMinutes(
          parseISO(applicationB?.attributes.updated_at),
          parseISO(applicationA?.attributes.updated_at),
        );
      })
    : [];

  const policy = getIncludedResource(resource.data, includedResources, 'policy');
  const schemes = getIncludedResource(resource.data, includedResources, 'schemes');

  const user = getIncludedResource(resource.data, includedResources, 'user');

  const broker = getIncludedResource(resource.data, includedResources, 'broker');

  const source = getIncludedResource(resource.data, includedResources, 'source');

  const mtas = getIncludedResource(
    resource.data,
    includedResources,
    'mid_term_adjustment_applications',
  );

  const fromResource =
    resource.data.type === 'policies/applications' ? resource.data : schemes[0];
  const product = getIncludedResource(fromResource, resource.included, 'product');

  const renewalPolicyApplications = getIncludedResource(
    resource.data,
    includedResources,
    'renewal_policy_application',
  );
  const transactions = includedResources.filter((included) => {
    return get(included, 'type') === 'policies/transactions';
  });

  const address = displayAddress(customer.data.attributes?.address);
  const campaignName = get(campaign, 'data.attributes.name');
  const resourceAttributes = get(resource, 'data.attributes');
  const isB2c = channel.data.attributes && channel.data.attributes.channel_type === 'B2C';
  const startDate = get(
    resourceAttributes,
    'starts_at',
    get(resourceAttributes, 'commences_at'),
  );
  const parsedStartDate = parseISO(startDate, new Date());
  const requiresBackDateCover = isPast(endOfDay(parsedStartDate));

  const status = getDerivedStatus(
    resourceAttributes.quote_type,
    resourceAttributes.status,
  );
  const isPolicy = resource.data.type === 'policies';

  return (
    <Box>
      <div className="policy-details">
        {isBrokerPolicy && <BrokerBanner />}
        <h2 className="resource-name">
          <span className="pull-left">{reference}</span>
          <Logo scheme={schemes && schemes.length ? schemes[0] : null} />
          <PolicyLabel
            status={resourceAttributes.status}
            policyNumber={resourceAttributes.policy_number}
            quoteType={resourceAttributes.quote_type}
            label={status}
          />
        </h2>
        <Summary
          resource={resourceAttributes}
          status={status}
          policy={policy}
          premiums={premiums.data ? [] : premiums}
          productCode={get(product, 'data.attributes.product_code')}
        />
        <CustomerDetails
          customer={customer}
          address={address}
          handleCreateUser={handleCreateUser}
          communications={resourceAttributes.metadata.communications}
          productCode={get(product, 'data.attributes.product_code')}
        />

        <Travellers
          resource={resource}
          productCode={get(product, 'data.attributes.product_code')}
        />

        <History
          show={isOwner || isBroker()}
          campaignName={campaignName}
          applications={sortedIssuedApplications}
          mtas={mtas}
          resource={resourceAttributes}
          policy={policy}
          broker={broker}
          source={
            !hasRole('Standard (Claims Handler)') &&
            !hasRole('Standard (Assistance Handler)') &&
            !hasRole('Standard (Insurer)') &&
            source
          }
          user={user}
          isPolicy={isPolicy}
          renewalPolicyApplications={renewalPolicyApplications}
        />
        <PolicyHistory status={resourceAttributes.status} quotesHistory={quotesHistory} />
        <Endorsements
          endorsements={get(resourceAttributes, 'metadata.storage.endorsements')}
        />
        <Transactions transactions={transactions} />
        <Premiums
          isPolicy={isPolicy}
          premiums={premiums.data ? [] : premiums}
          isB2C={isB2c}
          status={resource.status}
          resource={resource}
          showButtons={showButtons(
            resourceAttributes.valid,
            requiresBackDateCover,
            resourceAttributes.quote_type,
            resourceAttributes.status,
          )}
          showBackDateCover={
            requiresBackDateCover && resourceAttributes.status === 'quote'
          }
          brokerId={broker.data.id}
          product={product}
          declarationText={declarationText}
        />
        <RequiredFields
          status={resourceAttributes.status}
          isValid={resourceAttributes.valid}
          validationErrors={resourceAttributes.validation}
        />
        <div className="hr transparent" />
        <Buttons
          resource={resource}
          user={user}
          schemes={schemes}
          product={product}
          customer={customer}
          broker={broker}
          handleUncancel={handleUncancel}
          handleEndorsementEmail={handleEndorsementEmail}
          handleCustomerLogin={handleCustomerLogin}
          handleEditQuote={handleEditQuote}
        />
      </div>
    </Box>
  );
};
export default connect()(PolicyDetails);
